// A generic focus-trapped panel shell for the Money screen (component-designs.md §2.5:
// "reuses GdprRoutingPanel's shell"). `GdprRoutingPanel.tsx` itself is NOT reused
// directly - it hardcodes Certify's own copy keys and a fixed "list of items with a
// confirm button per item" body (GdprRoutingPanel.tsx:108-123), the wrong shape for a
// single `Field`-based form (the billing-inbox entry form, the ceiling-change confirm).
// This component instead reuses the exact SAME visual/interaction pattern
// (`.gdpr-routing-panel > .card.pad`, `role="dialog"`, the identical focus-trap logic)
// with arbitrary `children` - the same generalization `BuildConfirmDialog.tsx` already
// applied for a simpler two-button confirm (Rule of Three: this is the third instance of
// the identical focus-trap logic, app-engineering §1).
import { useEffect, useId, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const MoneyPanel = ({
  heading,
  children,
  onClose,
  triggerRef,
  testId,
}: {
  readonly heading: string;
  readonly children: React.ReactNode;
  /** Escape and any explicit "Cancel" control both call this - always exits without
   * side effects (`escape-routes`). */
  readonly onClose: () => void;
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
  readonly testId?: string;
}): React.JSX.Element => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();

  useEffect(() => {
    headingRef.current?.focus();
    return () => {
      triggerRef?.current?.focus();
    };
  }, [triggerRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab" || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="gdpr-routing-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      ref={panelRef}
      onKeyDown={handleKeyDown}
      data-testid={testId ?? "money-panel"}
    >
      <div className="card pad">
        <h2 className="h3" id={headingId} tabIndex={-1} ref={headingRef}>
          {heading}
        </h2>
        {children}
      </div>
    </div>
  );
};
